import React, { useState, useContext, useEffect } from "react";
import classNames from "classnames";
import { Link } from "react-router-dom";
import { Button } from "Components/ReactstrapSlim";
import { OwnerDetailsModal, OwnerCancelModal } from "Components/HourlyRequest";
import { Module, ModuleCaption } from "Components/Module";
import { EnterpriseAccountMemberReservationClient, ITeamMemberReservation, ReservationsClient, ReservationTimeType } from "Services/API/API";
import { useApiClient } from "Services/API/useApiClient";
import { isPlural } from "Utils/isPlural";
import { SearchContext } from "../Main/SearchContextProvider";
import { ReservationRow } from "../ReservationRow";
import AccountContext from "../../AccountContext";
import UserContext from "../../UserContext";
import moment from "moment";
import { ReservationDetailsModal } from "../Reservations/DetailsModal";

export interface ReservationModuleElement {
   refresh(): void
}

const DISPLAY_COUNT = 3;

export const ReservationsModule = React.forwardRef<ReservationModuleElement>((_, ref) => {
   const account = useContext(AccountContext);
   const { user } = useContext(UserContext);
   const searchManager = useContext(SearchContext);
   const [isLoading, setIsLoading] = useState<boolean>(true);
   const [reservations, setReservations] = useState<ITeamMemberReservation[]>([]);
   const [hrtbCancelRequestId, setHrtbCancelRequestId] = useState<Guid | undefined>(undefined);
   const [hrtbDetailsRequestId, setHrtbDetailsRequestId] = useState<Guid | undefined>(undefined);
   const [reservationDetails, setReservationDetails] = useState<ITeamMemberReservation | undefined>(undefined);

   const isNullState = !isLoading && !reservations.length;

   const enterpriseMemberReservationClient = useApiClient(EnterpriseAccountMemberReservationClient);

   const updateData = async () => {
      const upcomingResult = await enterpriseMemberReservationClient.reservations(account.id, user.id, ReservationTimeType.Future, undefined, true);
      setReservations(upcomingResult);
   }

   const reservationClient = useApiClient(ReservationsClient);
   const reservationChangeHandler = async (reservation: ITeamMemberReservation) => {
      const updatedReservation = await reservationClient.get(reservation.idForLink, reservation.occurrenceDate, undefined);
      const newReservations = reservations.map(item => {
         if (item.idForLink == updatedReservation.idForLink && (!updatedReservation.occurrenceDate || moment(updatedReservation.occurrenceDate).isSame(item.occurrenceDate))) {
            return updatedReservation;
         }
         return item;
      });
      setReservations(newReservations);
   }

   useEffect(() => {
      if (ref) {
         const refObj = {
            refresh: () => {
               setIsLoading(true);
               enterpriseMemberReservationClient.reservations(account.id, user.id, ReservationTimeType.Future, undefined, true)
                  .then(data => {
                     setReservations(data);
                     setIsLoading(false);
                  });
            }
         }

         if (typeof ref === "function") {
            ref(refObj);
         }
         else if ("current" in ref) {
            ref.current = refObj;
         }
      }
   }, [ref]);

   useEffect(() => {
      const abortController = new AbortController();
      enterpriseMemberReservationClient.reservations(account.id, user.id, ReservationTimeType.Future, undefined, true, abortController.signal)
         .then(data => {
            setReservations(data);
            setIsLoading(false);
         });

      return function cleanup() {
         abortController.abort();
      }
   }, []);

   return (<Module id="upcoming-section-id" size="lg" className={classNames("reservations-module", { "null-state": isNullState })} loading={isLoading} style={{ minHeight: "200px" }}>
      {isNullState
         ? <>
            <ModuleCaption className="mb-px8">No upcoming reservations</ModuleCaption>
            <div className="mb-px24">Your upcoming reservations will appear here once booked.</div>
            <Button color="arctic-20" size="sm" onClick={event => window.open(searchManager.currentSearchLink(), "_blank")}>Explore spaces nearby</Button>
         </>
         : <>
            <ModuleCaption className="mb-px24">{reservations.length} Upcoming {isPlural(reservations.length) ? "Reservations" : "Reservation"}</ModuleCaption>
            <div className="dashboard-reservations">
               {reservations.sort((a, b) => moment(a.startTime).diff(b.startTime))
                  .slice(0, DISPLAY_COUNT)
                  .map(reservation => <ReservationRow key={reservation.id} reservationType={ReservationTimeType.Future} reservation={reservation} onUpdateReservationList={updateData} onChange={reservationChangeHandler} onHourlyRequestToBookCancel={setHrtbCancelRequestId} onHourlyRequestToBookDetails={setHrtbDetailsRequestId} onReservationDetails={setReservationDetails} />)}
            </div>
            {reservations.length > DISPLAY_COUNT ? <div className="dashboard-reservations-view-all"><Link to="/reservations">View All</Link></div> : null}
            {hrtbCancelRequestId && <OwnerCancelModal requestId={hrtbCancelRequestId} isOpen={!!hrtbCancelRequestId} toggle={() => setHrtbCancelRequestId(undefined)} onSuccess={() => updateData()} />}
            {hrtbDetailsRequestId && <OwnerDetailsModal requestId={hrtbDetailsRequestId} isOpen={!!hrtbDetailsRequestId} toggle={() => setHrtbDetailsRequestId(undefined)} />}
            {reservationDetails && <ReservationDetailsModal reservation={reservationDetails} isOpen={!!reservationDetails} toggle={() => setReservationDetails(undefined)} onUpdateReservationList={updateData} onUpdateReservation={() => reservationChangeHandler(reservationDetails)} />}
         </>}
   </Module>);
});